/** QuickHouse — Charpente : poutres (horizontales ou rampantes) et poteaux.
 *  Géométrie pure (sans three) : résolution des défauts, boîtes 3D, hit-test 2D. */
import { dist, distPointToSegment } from './geometry.js';

export const BEAM_MATERIAL_LABELS = {
  wood: 'Bois (chêne)',
  'wood-dark': 'Bois foncé',
  steel: 'Acier anthracite',
  white: 'Laqué blanc',
};

/** Résout les hauteurs d'une poutre : elevA/elevB null → dessous du plafond */
export function beamElevations(beam, floorHeight) {
  const elevA = beam.elevA ?? floorHeight - beam.h;
  return { elevA, elevB: beam.elevB ?? elevA };
}

/** Résout la hauteur d'un poteau : null → hauteur de l'étage (moins sa base) */
export function postHeight(post, floorHeight) {
  return post.height ?? Math.max(10, floorHeight - post.elevation);
}

/** Boîte 3D d'un élément de charpente, en cm dans le repère du plan.
 *  Poutre : { kind:'beam', mid, len, angle (rad, plan), w, h, elev (dessous au milieu), tilt (rad) }
 *  Poteau : { kind:'post', p, w, d, height, elevation } */
export function beamBox(beam, floorHeight) {
  if (beam.kind === 'post') {
    return {
      kind: 'post',
      p: beam.p,
      w: beam.w,
      d: beam.d ?? beam.w,
      height: postHeight(beam, floorHeight),
      elevation: beam.elevation,
    };
  }
  const { elevA, elevB } = beamElevations(beam, floorHeight);
  const run = dist(beam.a, beam.b);
  const rise = elevB - elevA;
  return {
    kind: 'beam',
    mid: { x: (beam.a.x + beam.b.x) / 2, y: (beam.a.y + beam.b.y) / 2 },
    len: Math.hypot(run, rise),
    angle: Math.atan2(beam.b.y - beam.a.y, beam.b.x - beam.a.x),
    tilt: Math.atan2(rise, run), // rampant : rotation autour de l'axe transversal
    w: beam.w,
    h: beam.h,
    elev: (elevA + elevB) / 2,
  };
}

/** Hit-test 2D (tolérance en cm) */
export function hitBeam(beam, p, tol) {
  if (beam.kind === 'post') {
    const d = beam.d ?? beam.w;
    return Math.abs(p.x - beam.p.x) <= beam.w / 2 + tol && Math.abs(p.y - beam.p.y) <= d / 2 + tol;
  }
  return distPointToSegment(p, beam.a, beam.b) <= beam.w / 2 + tol;
}

export function translateBeam(beam, dx, dy) {
  if (beam.kind === 'post') {
    beam.p = { x: beam.p.x + dx, y: beam.p.y + dy };
  } else {
    beam.a = { x: beam.a.x + dx, y: beam.a.y + dy };
    beam.b = { x: beam.b.x + dx, y: beam.b.y + dy };
  }
}

export function beamOrigin(beam) {
  return beam.kind === 'post' ? beam.p : beam.a;
}
